import React from 'react';
import { ApolloError } from '@apollo/client';
import { Link } from 'react-router-dom';

import { Button, EditIcon } from '@occc/ui-design-system';
import { useAppLayoutContextConsumer, Page } from '@occc-app/design-system';
import { BannerConfigurationFragment, DisplayCategoryFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { DisplayCategoryDetails } from './DisplayCategoryDetails';
import { getBreadcrumbLinks } from './utils';

import { routeCreator } from '../utils';

type DisplayCategoryDetailsPageProps = {
  loading: boolean;
  error?: ApolloError;
  displayCategory?: DisplayCategoryFragment | null;
  externalBanners?: BannerConfigurationFragment[] | null;
};

export const DisplayCategoryDetailsPage = ({
  loading,
  error,
  displayCategory,
  externalBanners,
}: DisplayCategoryDetailsPageProps) => {
  const displayCategoryName = displayCategory?.name;
  const links = React.useMemo(() => getBreadcrumbLinks(displayCategoryName), [displayCategoryName]);

  useAppLayoutContextConsumer(links);

  const isUserPermittedToEditDisplayCategory = useIsUserPermittedTo(Feature.DISPLAY_CATEGORY_EDIT);
  const isEditButtonVisible = isUserPermittedToEditDisplayCategory && displayCategory;

  return (
    <Page
      browserTabTitle='Display Category'
      containerProps={{ variant: 'md' }}
      pageHeaderProps={{
        title: 'Display Category Details',
      }}
      actions={
        isEditButtonVisible && (
          <Button
            color='secondary'
            variant='contained'
            component={Link}
            to={routeCreator.update(displayCategory.id)}
            startIcon={<EditIcon />}
            role='button'
          >
            Edit
          </Button>
        )
      }
    >
      <DisplayCategoryDetails
        loading={loading}
        error={error}
        displayCategory={displayCategory}
        externalBanners={externalBanners}
      />
    </Page>
  );
};
